import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./OrganizationEmailInbox.scss";
import {
  listInboundByOrganization,
  listInboundByModuleRecord,
  getInboundDetail,
  markInboundRead,
  sendOutboundTransactional,
  getOperationalDiagnostics,
} from "../../../services/emailCorrespondenceApi";
import { useUserData } from "../../../hooks/useUserData";
import { resolveEmailPlatformOrganizationGuid } from "../../../utils/emailPlatformOrganization";

function apiOk(res) {
  return res && (res.success === 1 || res.success === "1");
}

function readIsReadFlag(row) {
  if (!row) return false;
  return !!(row.IsRead ?? row.isRead);
}

const OrganizationEmailInbox = () => {
  const { userData } = useUserData();

  const [organizationRefId, setOrganizationRefId] = useState("");
  const [take, setTake] = useState(50);
  const [listMode, setListMode] = useState("organization");
  const [moduleFilter, setModuleFilter] = useState("");
  const [recordFilter, setRecordFilter] = useState("");
  const [includeBodies, setIncludeBodies] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const [rows, setRows] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [diag, setDiag] = useState(null);

  const [toAddress, setToAddress] = useState("");
  const [displayFromName, setDisplayFromName] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");

  const staffReadBy = useMemo(
    () => String(userData?.StaffId || "").trim(),
    [userData?.StaffId],
  );
  const sessionOrganizationRefId = useMemo(
    () => resolveEmailPlatformOrganizationGuid(userData),
    [userData],
  );
  const sessionOrganizationName = useMemo(() => {
    if (!userData) return "";
    const candidates = [
      userData.OrganizationName,
      userData.organizationName,
      userData.OrgName,
      userData.orgName,
    ];
    const value = candidates.find((v) => String(v || "").trim());
    return String(value || "").trim();
  }, [userData]);

  useEffect(() => {
    if (!organizationRefId && sessionOrganizationRefId) {
      setOrganizationRefId(sessionOrganizationRefId);
    }
  }, [organizationRefId, sessionOrganizationRefId]);

  useEffect(() => {
    if (!displayFromName && sessionOrganizationName) {
      setDisplayFromName(sessionOrganizationName);
    }
  }, [displayFromName, sessionOrganizationName]);

  const refreshDiag = useCallback(async () => {
    try {
      const res = await getOperationalDiagnostics();
      if (apiOk(res)) {
        setDiag(res.data ?? null);
      } else {
        setDiag(null);
      }
    } catch {
      setDiag(null);
    }
  }, []);

  const loadInbox = useCallback(async () => {
    setLoadingInbox(true);
    setError("");
    try {
      let res;
      if (listMode === "moduleRecord") {
        res = await listInboundByModuleRecord({
          organizationRefId,
          module: moduleFilter,
          recordId: recordFilter,
          take,
          includeBodies,
        });
      } else {
        res = await listInboundByOrganization(organizationRefId, take);
      }

      if (!apiOk(res)) {
        setRows([]);
        setError(res?.message || "Could not load inbox.");
        return;
      }

      const list = Array.isArray(res.data) ? res.data : [];
      setRows(list);
    } catch (e) {
      setRows([]);
      setError(e?.message || "Failed to load inbox.");
    } finally {
      setLoadingInbox(false);
    }
  }, [
    includeBodies,
    listMode,
    moduleFilter,
    organizationRefId,
    recordFilter,
    take,
  ]);

  useEffect(() => {
    refreshDiag();
  }, [refreshDiag]);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  const visibleRows = useMemo(() => {
    if (!unreadOnly) return rows;
    return rows.filter((r) => !readIsReadFlag(r));
  }, [rows, unreadOnly]);

  async function openDetail(id) {
    if (!id) return;
    setSelectedId(id);
    setLoadingDetail(true);
    setError("");
    try {
      const res = await getInboundDetail(organizationRefId, id);
      if (!apiOk(res)) {
        setDetail(null);
        setError(res?.message || "Could not load message detail.");
        return;
      }
      setDetail(res.data || null);
    } catch (e) {
      setDetail(null);
      setError(e?.message || "Detail request failed.");
    } finally {
      setLoadingDetail(false);
    }
  }

  async function handleMarkRead(id, isRead) {
    if (!id) return;
    setError("");
    try {
      const res = await markInboundRead(organizationRefId, id, {
        isRead,
        readByStaffId: staffReadBy,
      });
      if (!apiOk(res)) {
        setError(res?.message || "Could not update read state.");
        return;
      }
      await loadInbox();
      if (selectedId === id) {
        await openDetail(id);
      }
    } catch (e) {
      setError(e?.message || "Mark read failed.");
    }
  }

  async function handleSend() {
    setError("");
    setSuccess("");
    try {
      const res = await sendOutboundTransactional({
        organizationRefId,
        module: moduleFilter,
        recordId: recordFilter,
        toAddress,
        displayFromName,
        subject,
        bodyText,
        bodyHtml,
      });
      if (!apiOk(res)) {
        setError(res?.message || "Send was not accepted.");
        return;
      }
      const sendData = res.data;
      const detailMsg =
        sendData?.DetailMessage ||
        sendData?.detailMessage ||
        "Message submitted to Azure Communication Services.";
      const replyToUsed =
        sendData?.ReplyToAddress || sendData?.replyToAddress || "";
      setSuccess(replyToUsed ? `${detailMsg} Reply-To: ${replyToUsed}` : detailMsg);
    } catch (e) {
      setError(e?.message || "Send failed.");
    }
  }

  const headerFields = detail?.Header || detail?.header || null;
  const bodyFields = detail?.Body || detail?.body || null;
  const attachments = detail?.Attachments || detail?.attachments || [];

  return (
    <div className="organization-email-inbox container-fluid py-4">
      <div className="mb-4">
        <h1 className="h3 mb-1">Organization email</h1>
        <p className="text-muted mb-0">
          Inbound mail is ingested via SendGrid Inbound Parse posting to{" "}
          <code>POST /api/EmailInboundWebhook/SendGridInbound</code> (optional{" "}
          <code>?key=…</code> or header <code>X-Email-Platform-Key</code> when{" "}
          <code>EmailPlatform.Inbound.WebhookSecret</code> is set). Outbound
          sends use Azure Communication Services.
        </p>
      </div>

      {error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      ) : null}

      {diag ? (
        <div className="card mb-4 border-secondary-subtle">
          <div className="card-body py-2">
            <div className="small text-muted mb-1">Configuration (API)</div>
            <div className="d-flex flex-wrap gap-3 small">
              <span>
                ACS secret (vault/env):{" "}
                <strong>
                  {diag.AzureCommunicationConfigured ||
                  diag.azureCommunicationConfigured
                    ? "present"
                    : "missing"}
                </strong>
              </span>
              <span>
                Webhook authentication:{" "}
                <strong>
                  {diag.InboundWebhookSecretConfigured ||
                  diag.inboundWebhookSecretConfigured
                    ? "Secret is set — caller must supply key/header"
                    : "No secret — endpoint is publicly reachable"}
                </strong>
              </span>
              <span>
                Default organization:{" "}
                <strong>
                  {diag.DefaultOrganizationConfigured ||
                  diag.defaultOrganizationConfigured
                    ? "set"
                    : "not set"}
                </strong>
              </span>
              <span>
                Outbound From:{" "}
                <strong>
                  {diag.OutboundFromAddressConfigured ||
                  diag.outboundFromAddressConfigured
                    ? "configured"
                    : "missing"}
                </strong>
              </span>
            </div>
          </div>
        </div>
      ) : null}

      <div className="row g-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-white fw-semibold">Filters</div>
            <div className="card-body row g-3">
              <div className="col-md-4">
                <label className="form-label">OrganizationRefId (GUID)</label>
                <input
                  className="form-control"
                  value={organizationRefId}
                  onChange={(e) => setOrganizationRefId(e.target.value)}
                  placeholder="Required unless DefaultOrganizationRefId is set server-side."
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Row limit</label>
                <input
                  type="number"
                  min={1}
                  max={500}
                  className="form-control"
                  value={take}
                  onChange={(e) => setTake(Number(e.target.value) || 50)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">List mode</label>
                <select
                  className="form-select"
                  value={listMode}
                  onChange={(e) => setListMode(e.target.value)}
                >
                  <option value="organization">All mail for organization</option>
                  <option value="moduleRecord">Filtered by Module + RecordId</option>
                </select>
              </div>
              <div className="col-md-3 d-flex align-items-end gap-3 flex-wrap">
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="unreadOnly"
                    checked={unreadOnly}
                    onChange={(e) => setUnreadOnly(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="unreadOnly">
                    Unread only (client-side)
                  </label>
                </div>
              </div>
              {listMode === "moduleRecord" ? (
                <>
                  <div className="col-md-4">
                    <label className="form-label">Module</label>
                    <input
                      className="form-control"
                      value={moduleFilter}
                      onChange={(e) => setModuleFilter(e.target.value)}
                      placeholder="e.g. CAPA"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">RecordId</label>
                    <input
                      className="form-control"
                      value={recordFilter}
                      onChange={(e) => setRecordFilter(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="incBodies"
                        checked={includeBodies}
                        onChange={(e) => setIncludeBodies(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="incBodies">
                        Include full bodies (joined table)
                      </label>
                    </div>
                  </div>
                </>
              ) : null}
              <div className="col-12">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  disabled={loadingInbox}
                  onClick={() => loadInbox()}
                >
                  {loadingInbox ? "Loading…" : "Apply filters / refresh"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card shadow-sm">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <span className="fw-semibold">
                Inbox ({visibleRows.length}
                {unreadOnly ? " unread shown" : " shown"})
              </span>
            </div>
            <div className="table-responsive">
              <table className="table table-hover mb-0 small align-middle">
                <thead>
                  <tr>
                    <th scope="col">UTC</th>
                    <th scope="col">Read</th>
                    <th scope="col">Source</th>
                    <th scope="col">From</th>
                    <th scope="col">Subject</th>
                    <th scope="col">Preview</th>
                    <th scope="col" className="text-end">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-muted text-center py-4">
                        No messages match the current filters.
                      </td>
                    </tr>
                  ) : (
                    visibleRows.map((row) => {
                      const id = row.Id || row.id || "";
                      const subjectRow = row.Subject || row.subject || "";
                      const from = row.From || row.from || "";
                      const source = row.SourceTag || row.sourceTag || "";
                      const recv = row.ReceivedUtc || row.receivedUtc || "";
                      const prev =
                        row.TextBodyPreview ||
                        row.textBodyPreview ||
                        row.HtmlBodyPreview ||
                        row.htmlBodyPreview ||
                        "";
                      const unread = !readIsReadFlag(row);
                      return (
                        <tr
                          key={id || subjectRow + from}
                          className={
                            selectedId === id ? "table-active" : undefined
                          }
                        >
                          <td className="text-nowrap">
                            {recv
                              ? new Date(recv).toLocaleString(undefined, {
                                  timeZone: "UTC",
                                })
                              : "—"}
                          </td>
                          <td>
                            {readIsReadFlag(row) ? (
                              <span className="badge bg-secondary">Read</span>
                            ) : (
                              <span className="badge bg-warning text-dark">
                                New
                              </span>
                            )}
                          </td>
                          <td>{source}</td>
                          <td>{from}</td>
                          <td>{subjectRow}</td>
                          <td className="text-truncate" style={{ maxWidth: 200 }} title={prev}>
                            {prev || "—"}
                          </td>
                          <td className="text-end text-nowrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-dark me-1"
                              onClick={() => openDetail(id)}
                            >
                              Open
                            </button>
                            {unread ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-dark"
                                onClick={() => handleMarkRead(id, true)}
                              >
                                Mark read
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleMarkRead(id, false)}
                              >
                                Mark unread
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card shadow-sm mb-3">
            <div className="card-header bg-white fw-semibold">Outbound (ACS)</div>
            <div className="card-body">
              <div className="mb-2">
                <label className="form-label">To</label>
                <input
                  className="form-control"
                  value={toAddress}
                  onChange={(e) => setToAddress(e.target.value)}
                  placeholder="recipient@domain.com"
                />
              </div>
              <div className="mb-2">
                <label className="form-label">Display from name (organization)</label>
                <input
                  className="form-control"
                  value={displayFromName}
                  onChange={(e) => setDisplayFromName(e.target.value)}
                  placeholder="e.g. Ascension Research - CAPA"
                />
                <div className="form-text">
                  Reply-To is generated by API as constant pattern using OrganizationRefId.
                </div>
              </div>
              <div className="mb-2">
                <label className="form-label">Subject</label>
                <input
                  className="form-control"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              <div className="mb-2">
                <label className="form-label">Body (plain text)</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Body HTML (optional)</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                />
              </div>
              <button type="button" className="btn btn-dark" onClick={handleSend}>
                Send
              </button>
            </div>
          </div>

          <div className="card shadow-sm detail-panel">
            <div className="card-header bg-white fw-semibold">
              Detail
              {loadingDetail ? <span className="text-muted ms-2">Loading…</span> : null}
            </div>
            <div className="card-body small">
              {!selectedId ? (
                <div className="text-muted">
                  Select a row and choose Open for headers, body, and attachments.
                </div>
              ) : !headerFields ? (
                <div className="text-muted">
                  Open a row to fetch full content from SQL.
                </div>
              ) : (
                <>
                  <div className="mb-2 fw-semibold">Header</div>
                  <dl className="row mb-0">
                    {Object.keys(headerFields).map((k) => (
                      <React.Fragment key={k}>
                        <dt className="col-sm-4 text-muted">{k}</dt>
                        <dd className="col-sm-8">
                          {formatCell(headerFields[k])}
                        </dd>
                      </React.Fragment>
                    ))}
                  </dl>
                  {bodyFields && Object.keys(bodyFields).length > 0 ? (
                    <>
                      <div className="mt-3 mb-2 fw-semibold">Body</div>
                      <pre className="bg-light p-2 rounded small">
                        {formatBodySnippet(bodyFields)}
                      </pre>
                    </>
                  ) : (
                    <p className="text-muted mt-2 mb-0">No stored body fragment.</p>
                  )}
                  {Array.isArray(attachments) && attachments.length > 0 ? (
                    <>
                      <div className="mt-3 mb-2 fw-semibold">Attachments</div>
                      <ul className="mb-0">
                        {attachments.map((a, idx) => (
                          <li key={idx}>
                            {(a.FileName || a.fileName || "file") +
                              " · " +
                              (a.ContentType ||
                                a.contentType ||
                                "unknown type")}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function formatCell(v) {
  if (v === null || v === undefined)
    return "—";
  if (typeof v === "object")
    return JSON.stringify(v);
  return String(v);
}

function formatBodySnippet(bodyFields) {
  const text =
    bodyFields.TextBody ||
    bodyFields.textBody ||
    bodyFields.HtmlBody ||
    bodyFields.htmlBody ||
    "";
  const max = 8000;
  const s = String(text);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

export default OrganizationEmailInbox;
